// LEGACY — superseded by features/global-users/. Scheduled for deletion in 2 sprints.
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { GlobalUserItem } from "../../interfaces/GlobalUser";
import { GlobalUsersResponse } from "./types";
import * as globalUserService from "../../services/globalUserService";
import { getLoggedinUser } from "../../helpers/api_helper";

interface GlobalUserState {
    items: GlobalUserItem[];
    selectedUser: GlobalUserItem | null;
    loading: boolean;
    error: string | null;
    pageNumber: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    // Profile-Based User State (RECOMMENDATION: Caching)
    fieldUsers: GlobalUserItem[]; // Cached Field Users (FieldWorker ProfileId)
    managers: GlobalUserItem[]; // Cached Managers (Supervisor/Manager ProfileIds)
    fieldUsersLoading: boolean;
    managersLoading: boolean;
    fieldUsersError: string | null;
    managersError: string | null;
}

const initialState: GlobalUserState = {
    items: [],
    selectedUser: null,
    loading: false,
    error: null,
    pageNumber: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0,
    // Profile-Based User State
    fieldUsers: [],
    managers: [],
    fieldUsersLoading: false,
    managersLoading: false,
    fieldUsersError: null,
    managersError: null,
};

// Async thunk to fetch global users from API
export const fetchGlobalUsers = createAsyncThunk(
    "globalUsers/fetchGlobalUsers",
    async ({
        pageNumber = 1,
        pageSize = 20,
    }: {
        pageNumber?: number;
        pageSize?: number;
    } = {}) => {
        // Get tenantId from logged-in user
        const authUser = getLoggedinUser();
        const tenantId = authUser?.tenantId;
        
        const response: GlobalUsersResponse = await globalUserService.getAllGlobalUsers(
            pageNumber,
            pageSize,
            tenantId
        );
        return response;
    }
);

// Async thunk to create a new global user
export const createGlobalUser = createAsyncThunk(
    "globalUsers/createGlobalUser",
    async (data: Partial<GlobalUserItem>) => {
        const response = await globalUserService.createGlobalUser(data);
        return response;
    }
);

// Async thunk to update an existing global user
export const updateGlobalUser = createAsyncThunk(
    "globalUsers/updateGlobalUser",
    async ({ id, data }: { id: string; data: Partial<GlobalUserItem> }) => {
        const response = await globalUserService.updateGlobalUser(id, data);
        return response;
    }
);

// Async thunk to delete a global user
export const deleteGlobalUser = createAsyncThunk(
    "globalUsers/deleteGlobalUser",
    async (id: string) => {
        await globalUserService.deleteGlobalUser(id);
        return id;
    }
);

/**
 * Fetch Tenant Field Users (Profile-Based)
 * Backend filters by FieldWorker ProfileId
 * RECOMMENDATION: Standardized page size of 200
 */
export const fetchTenantFieldUsers = createAsyncThunk(
    "globalUsers/fetchTenantFieldUsers",
    async ({
        pageNumber = 1,
        pageSize = 200, // RECOMMENDATION: Standardized page size
    }: {
        pageNumber?: number;
        pageSize?: number;
    } = {}) => {
        const authUser = getLoggedinUser();
        const tenantId = authUser?.tenantId;
        const response: GlobalUsersResponse = await globalUserService.getTenantFieldUsers(
            pageNumber,
            pageSize,
            tenantId
        );
        return response;
    }
);

/**
 * Fetch Tenant Managers (Profile-Based)
 * Backend filters by Supervisor/Manager ProfileIds
 * RECOMMENDATION: Standardized page size of 200
 */
export const fetchTenantManagers = createAsyncThunk(
    "globalUsers/fetchTenantManagers",
    async ({
        pageNumber = 1,
        pageSize = 200, // RECOMMENDATION: Standardized page size
    }: {
        pageNumber?: number;
        pageSize?: number;
    } = {}) => {
        const authUser = getLoggedinUser();
        const tenantId = authUser?.tenantId;
        const response: GlobalUsersResponse = await globalUserService.getTenantManagers(
            pageNumber,
            pageSize,
            tenantId
        );
        return response;
    }
);

/**
 * Fetch Users by Profile IDs (Profile-Based - POST)
 * Backend filters by multiple ProfileIds
 * RECOMMENDATION: For future development
 */
export const fetchUsersByProfileIds = createAsyncThunk(
    "globalUsers/fetchUsersByProfileIds",
    async ({
        profileIds,
        pageNumber = 1,
        pageSize = 200,
    }: {
        profileIds: string[]; // Array of ProfileId GUIDs
        pageNumber?: number;
        pageSize?: number;
    }) => {
        const authUser = getLoggedinUser();
        const tenantId = authUser?.tenantId;
        const response: GlobalUsersResponse = await globalUserService.getUsersByProfileIds(
            profileIds,
            pageNumber,
            pageSize,
            tenantId
        );
        return response;
    }
);

const globalUserSlice = createSlice({
    name: "GlobalUsers",
    initialState,
    reducers: {
        selectGlobalUser(state, action: PayloadAction<string>) {
            state.selectedUser =
                state.items.find((u) => u.id === action.payload) || null;
        },
        clearError(state) {
            state.error = null;
        },
        // RECOMMENDATION: Cache clearing actions for profile-based users
        clearFieldUsersCache(state) {
            state.fieldUsers = [];
        },
        clearManagersCache(state) {
            state.managers = [];
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch global users
            .addCase(fetchGlobalUsers.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchGlobalUsers.fulfilled, (state, action) => {
                state.loading = false;
                state.items = action.payload.items;
                state.pageNumber = action.payload.pageNumber;
                state.pageSize = action.payload.pageSize;
                state.totalCount = action.payload.totalCount;
                state.totalPages = action.payload.totalPages;
            })
            .addCase(fetchGlobalUsers.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || "Failed to fetch global users";
            })
            // Create global user
            .addCase(createGlobalUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createGlobalUser.fulfilled, (state, action) => {
                state.loading = false;
                state.items.unshift(action.payload);
                state.totalCount += 1;
            })
            .addCase(createGlobalUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || "Failed to create global user";
            })
            // Update global user
            .addCase(updateGlobalUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateGlobalUser.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.items.findIndex((u) => u.id === action.payload.id);
                if (index !== -1) {
                    state.items[index] = action.payload;
                }
            })
            .addCase(updateGlobalUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || "Failed to update global user";
            })
            // Delete global user
            .addCase(deleteGlobalUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteGlobalUser.fulfilled, (state, action) => {
                state.loading = false;
                state.items = state.items.filter((u) => u.id !== action.payload);
                state.totalCount -= 1;
            })
            .addCase(deleteGlobalUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || "Failed to delete global user";
            })
            // Profile-Based: Field Users reducers
            .addCase(fetchTenantFieldUsers.pending, (state) => {
                state.fieldUsersLoading = true;
                state.fieldUsersError = null;
            })
            .addCase(fetchTenantFieldUsers.fulfilled, (state, action) => {
                state.fieldUsersLoading = false;
                state.fieldUsers = action.payload.items; // RECOMMENDATION: Cache results
                state.fieldUsersError = null;
            })
            .addCase(fetchTenantFieldUsers.rejected, (state, action) => {
                state.fieldUsersLoading = false;
                state.fieldUsersError = action.error.message || "Failed to fetch field users";
            })
            // Profile-Based: Managers reducers
            .addCase(fetchTenantManagers.pending, (state) => {
                state.managersLoading = true;
                state.managersError = null;
            })
            .addCase(fetchTenantManagers.fulfilled, (state, action) => {
                state.managersLoading = false;
                state.managers = action.payload.items; // RECOMMENDATION: Cache results
                state.managersError = null;
            })
            .addCase(fetchTenantManagers.rejected, (state, action) => {
                state.managersLoading = false;
                state.managersError = action.error.message || "Failed to fetch managers";
            })
            // Profile-Based: Users by Profile IDs reducers (stores in managers for compatibility)
            .addCase(fetchUsersByProfileIds.pending, (state) => {
                state.managersLoading = true;
                state.managersError = null;
            })
            .addCase(fetchUsersByProfileIds.fulfilled, (state, action) => {
                state.managersLoading = false;
                state.managers = action.payload.items; // Store in managers for compatibility with selectManagers
                state.managersError = null;
            })
            .addCase(fetchUsersByProfileIds.rejected, (state, action) => {
                state.managersLoading = false;
                state.managersError = action.error.message || "Failed to fetch users by profile IDs";
            });
    },
});

export const { selectGlobalUser, clearError, clearFieldUsersCache, clearManagersCache } = globalUserSlice.actions;
export default globalUserSlice.reducer;

// Selectors
export const selectGlobalUserList = (state: any) => state.GlobalUsers?.items || [];
export const selectSelectedGlobalUser = (state: any) =>
    state.GlobalUsers?.selectedUser || null;
export const selectGlobalUserById = (state: any, id: string) =>
    state.GlobalUsers?.items?.find((u: GlobalUserItem) => u.id === id) || null;
export const selectGlobalUserLoading = (state: any) => state.GlobalUsers?.loading || false;
export const selectGlobalUserError = (state: any) => state.GlobalUsers?.error || null;
export const selectGlobalUserTotalCount = (state: any) =>
    state.GlobalUsers?.totalCount || 0;

// Profile-Based Selectors
export const selectFieldUsers = (state: any) => state.GlobalUsers?.fieldUsers || [];
export const selectManagers = (state: any) => state.GlobalUsers?.managers || [];
export const selectFieldUsersLoading = (state: any) => state.GlobalUsers?.fieldUsersLoading || false;
export const selectManagersLoading = (state: any) => state.GlobalUsers?.managersLoading || false;
export const selectFieldUsersError = (state: any) => state.GlobalUsers?.fieldUsersError || null;
export const selectManagersError = (state: any) => state.GlobalUsers?.managersError || null;
